/**
 * 開発環境専用ツールページ
 *
 * Stripeテストクロックの管理など
 * 開発環境でのみ表示される
 */

import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  List,
  App,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  ClockCircleOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  FastForwardOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { trpc } from '../../lib/api';
import { withDevModalFilePath } from '../../components/common';

const { Title, Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/admin/DevToolsPage.tsx');

const isDevelopment = import.meta.env.DEV;

export function DevToolsPage() {
  const { message } = App.useApp();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [advanceDate, setAdvanceDate] = useState<dayjs.Dayjs | null>(null);

  // クエリ
  const testClocksQuery = trpc.devTools.listTestClocks.useQuery(undefined, {
    enabled: isDevelopment,
  });

  const companiesQuery = trpc.devTools.listCompaniesWithStripe.useQuery(undefined, {
    enabled: isDevelopment,
  });

  const companyStatusQuery = trpc.devTools.getCompanyStripeStatus.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: isDevelopment && !!selectedCompanyId }
  );

  // ミューテーション
  const advanceTestClockMutation = trpc.devTools.advanceTestClock.useMutation({
    onSuccess: (data) => {
      message.success(`テストクロックを ${data.newTime} に進めました`);
      testClocksQuery.refetch();
      companyStatusQuery.refetch();
    },
    onError: (error) => {
      message.error(`エラー: ${error.message}`);
    },
  });

  const deleteTestClockMutation = trpc.devTools.deleteTestClock.useMutation({
    onSuccess: () => {
      message.success('テストクロックを削除しました');
      testClocksQuery.refetch();
    },
    onError: (error) => {
      message.error(`エラー: ${error.message}`);
    },
  });

  if (!isDevelopment) {
    return (
      <Result
        status="403"
        title="開発環境でのみ利用可能"
        subTitle="この機能は開発環境でのみ利用できます。"
      />
    );
  }

  const handleAdvanceTestClock = (testClockId: string) => {
    if (!advanceDate) {
      message.warning('進める日時を選択してください');
      return;
    }

    Modal.confirm({
      title: 'テストクロックを進めますか？',
      content: (
        <div>
          <p>テストクロックの時間を進めます。この操作は元に戻せません。</p>
          <p>
            <strong>進める日時:</strong> {advanceDate.format('YYYY-MM-DD HH:mm')}
          </p>
        </div>
      ),
      onOk: () => {
        advanceTestClockMutation.mutate({
          testClockId,
          targetDate: advanceDate.toISOString(),
        });
      },
    });
  };

  const handleDeleteTestClock = (testClockId: string) => {
    Modal.confirm({
      title: 'テストクロックを削除しますか？',
      content: '関連するすべてのテストデータも削除されます。この操作は元に戻せません。',
      okType: 'danger',
      onOk: () => {
        deleteTestClockMutation.mutate({ testClockId });
      },
    });
  };

  const testClockColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => <Text code>{id}</Text>,
    },
    {
      title: '名前',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '現在時刻',
      dataIndex: 'frozenTime',
      key: 'frozenTime',
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'ready' ? 'green' : 'orange'}>{status}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: unknown, record: { id: string }) => (
        <Space>
          <Button
            icon={<FastForwardOutlined />}
            onClick={() => handleAdvanceTestClock(record.id)}
            loading={advanceTestClockMutation.isPending}
            disabled={!advanceDate}
          >
            時間進行
          </Button>
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDeleteTestClock(record.id)}
            loading={deleteTestClockMutation.isPending}
          >
            削除
          </Button>
        </Space>
      ),
    },
  ];

  const companyStatus = companyStatusQuery.data;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Title level={2} style={{ marginBottom: 0 }}>
          <ExperimentOutlined /> 開発ツール
        </Title>
        <Text type="secondary">
          Stripeテストクロック管理など開発環境専用のツールです。
        </Text>
      </Space>

      <Alert
        type="warning"
        showIcon
        message="開発環境専用"
        description="このページは開発環境でのみ利用可能です。本番環境ではアクセスできません。"
      />

      <Tabs
        items={[
          {
            key: 'test-clocks',
            label: (
              <span>
                <ClockCircleOutlined /> テストクロック
              </span>
            ),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Card title="テストクロック一覧" extra={
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => testClocksQuery.refetch()}
                  >
                    更新
                  </Button>
                }>
                  <Space style={{ marginBottom: 16 }}>
                    <DatePicker
                      showTime
                      placeholder="進める日時を選択"
                      value={advanceDate}
                      onChange={setAdvanceDate}
                    />
                  </Space>

                  {testClocksQuery.isLoading ? (
                    <Spin />
                  ) : (
                    <Table
                      dataSource={testClocksQuery.data || []}
                      columns={testClockColumns}
                      rowKey="id"
                      pagination={false}
                    />
                  )}
                </Card>
              </Space>
            ),
          },
          {
            key: 'company-status',
            label: '会社別Stripe状態',
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Card title="会社選択">
                  <Select
                    style={{ width: '100%' }}
                    placeholder="会社を選択してください"
                    value={selectedCompanyId}
                    onChange={setSelectedCompanyId}
                    options={companiesQuery.data?.map((c) => ({
                      value: c.id,
                      label: `${c.name} (${c.subscriptionStatus || '未設定'})`,
                    }))}
                    showSearch
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Card>

                {companyStatusQuery.isLoading && <Spin />}

                {companyStatus && (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <Card title="会社情報">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="会社名">
                            {companyStatus.company.name}
                          </Descriptions.Item>
                          <Descriptions.Item label="Stripe顧客ID">
                            <Text code>{companyStatus.company.stripeCustomerId}</Text>
                          </Descriptions.Item>
                          <Descriptions.Item label="サブスクリプションID">
                            <Text code>{companyStatus.company.stripeSubscriptionId}</Text>
                          </Descriptions.Item>
                          <Descriptions.Item label="ステータス">
                            <Tag
                              color={
                                companyStatus.company.subscriptionStatus === 'active'
                                  ? 'green'
                                  : 'orange'
                              }
                            >
                              {companyStatus.company.subscriptionStatus}
                            </Tag>
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Card title="テストクロック">
                        {companyStatus.testClock ? (
                          <Descriptions column={1} size="small">
                            <Descriptions.Item label="テストクロックID">
                              <Text code>{companyStatus.testClock.id}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="現在時刻">
                              {dayjs(companyStatus.testClock.frozenTime).format(
                                'YYYY-MM-DD HH:mm'
                              )}
                            </Descriptions.Item>
                            <Descriptions.Item label="ステータス">
                              <Tag color="green">{companyStatus.testClock.status}</Tag>
                            </Descriptions.Item>
                          </Descriptions>
                        ) : (
                          <Text type="secondary">テストクロックは設定されていません</Text>
                        )}
                      </Card>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Card title="契約情報">
                        {companyStatus.contract ? (
                          <>
                            <Descriptions column={1} size="small">
                              <Descriptions.Item label="プラン">
                                <Tag>{companyStatus.contract.planCode}</Tag>
                              </Descriptions.Item>
                              <Descriptions.Item label="ステータス">
                                <Tag
                                  color={
                                    companyStatus.contract.status === 'active'
                                      ? 'green'
                                      : 'orange'
                                  }
                                >
                                  {companyStatus.contract.status}
                                </Tag>
                              </Descriptions.Item>
                              <Descriptions.Item label="次回請求日">
                                {companyStatus.contract.nextInvoiceDate
                                  ? dayjs(companyStatus.contract.nextInvoiceDate).format(
                                      'YYYY-MM-DD'
                                    )
                                  : '-'}
                              </Descriptions.Item>
                            </Descriptions>

                            <Divider>追加オプション</Divider>
                            {Array.isArray(companyStatus.contract.addOns) &&
                            companyStatus.contract.addOns.length > 0 ? (
                              <List
                                size="small"
                                dataSource={companyStatus.contract.addOns as Array<{
                                  type: string;
                                  quantity: number;
                                }>}
                                renderItem={(item) => (
                                  <List.Item>
                                    <Tag color="blue">{item.type}</Tag> {item.quantity}
                                  </List.Item>
                                )}
                              />
                            ) : (
                              <Text type="secondary">追加オプションなし</Text>
                            )}
                          </>
                        ) : (
                          <Text type="secondary">契約情報がありません</Text>
                        )}
                      </Card>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Card title="Stripeサブスクリプション">
                        {companyStatus.stripeSubscription ? (
                          <>
                            <Descriptions column={1} size="small">
                              <Descriptions.Item label="ステータス">
                                <Tag
                                  color={
                                    companyStatus.stripeSubscription.status === 'active'
                                      ? 'green'
                                      : 'orange'
                                  }
                                >
                                  {companyStatus.stripeSubscription.status}
                                </Tag>
                              </Descriptions.Item>
                              <Descriptions.Item label="現在期間">
                                {dayjs(
                                  companyStatus.stripeSubscription.currentPeriodStart
                                ).format('YYYY-MM-DD')}
                                {' 〜 '}
                                {dayjs(
                                  companyStatus.stripeSubscription.currentPeriodEnd
                                ).format('YYYY-MM-DD')}
                              </Descriptions.Item>
                            </Descriptions>

                            <Divider>サブスクリプションアイテム</Divider>
                            <List
                              size="small"
                              dataSource={companyStatus.stripeSubscription.items}
                              renderItem={(rawItem) => {
                                const item = rawItem as {
                                  productName?: unknown;
                                  quantity?: unknown;
                                  amount?: unknown;
                                };

                                const quantity =
                                  typeof item.quantity === 'number'
                                    ? item.quantity
                                    : typeof item.quantity === 'string'
                                      ? Number(item.quantity)
                                      : null;

                                const amount =
                                  typeof item.amount === 'number'
                                    ? item.amount
                                    : typeof item.amount === 'string'
                                      ? Number(item.amount)
                                      : null;

                                return (
                                  <List.Item>
                                    <Space>
                                      <Text strong>{String(item.productName ?? '')}</Text>
                                      <Text type="secondary">
                                        x{quantity ?? '-'} @
                                        {typeof amount === 'number' && Number.isFinite(amount)
                                          ? `$${amount.toLocaleString()}`
                                          : '-'}
                                      </Text>
                                    </Space>
                                  </List.Item>
                                );
                              }}
                            />
                          </>
                        ) : (
                          <Text type="secondary">Stripeサブスクリプションがありません</Text>
                        )}
                      </Card>
                    </Col>

                    <Col xs={24}>
                      <Card title="請求書履歴">
                        <Table
                          size="small"
                          dataSource={companyStatus.invoices}
                          rowKey="id"
                          pagination={false}
                          columns={[
                            {
                              title: '請求書番号',
                              dataIndex: 'number',
                              key: 'number',
                            },
                            {
                              title: 'ステータス',
                              dataIndex: 'status',
                              key: 'status',
                              render: (status: string) => (
                                <Tag
                                  color={
                                    status === 'paid'
                                      ? 'green'
                                      : status === 'open'
                                      ? 'blue'
                                      : 'default'
                                  }
                                >
                                  {status}
                                </Tag>
                              ),
                            },
                            {
                              title: '金額',
                              dataIndex: 'total',
                              key: 'total',
                              render: (total: number) =>
                                `¥${(total / 100).toLocaleString()}`,
                            },
                            {
                              title: '請求理由',
                              dataIndex: 'billingReason',
                              key: 'billingReason',
                            },
                            {
                              title: '作成日',
                              dataIndex: 'created',
                              key: 'created',
                              render: (date: string) =>
                                dayjs(date).format('YYYY-MM-DD HH:mm'),
                            },
                          ]}
                        />
                      </Card>
                    </Col>
                  </Row>
                )}
              </Space>
            ),
          },
        ]}
      />
    </Space>
  );
}
